import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { adminApi } from '../api/adminApi';

const AuthContext = createContext(null);
const storageKeys = ['identity_admin_token', 'google_token', 'idt', 'rt', '_au', '_aup', '_auta', '_auta_s', 'gravatar', 'lwp'];
const cookieKeys = ['_au', '_aup', '_auta', '_auta_s', 'gravatar', 'google_token'];

export const clearIdentityStorage = () => {
  storageKeys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  cookieKeys.forEach((key) => {
    document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  });
};

export const AuthProvider = ({ children }) => {
  const [state, setState] = useState({ status: 'loading', user: null, message: '' });

  const refresh = useCallback(async () => {
    setState((current) => ({ ...current, status: 'loading' }));
    try {
      const result = await adminApi.session();
      const user = result.data?.actor;
      if (!user) throw new Error('The identity session response did not include an authenticated actor.');
      const access = Number(user.admin?.access ?? user.accessLevel ?? 0);
      setState({ status: access >= 10 ? 'authorized' : 'denied', user, expiresAt: result.data?.expiresAt || null, message: '' });
    } catch (error) {
      if (error.status === 403) setState({ status: 'denied', user: error.details?.user || null, message: error.message });
      else {
        clearIdentityStorage();
        setState({ status: 'signed-out', user: null, message: error.status === 401 ? '' : error.message });
      }
    }
  }, []);

  useEffect(() => {
    // The callback must finish its token exchange before session state is read;
    // otherwise a pre-exchange 401 can race and erase the projected handoff.
    if (window.location.pathname !== '/success') refresh();
  }, [refresh]);

  const signIn = async (credentials) => {
    await adminApi.signIn(credentials);
    await refresh();
  };

  const signOut = async () => {
    await adminApi.signOut();
    clearIdentityStorage();
    setState({ status: 'signed-out', user: null, message: '' });
  };

  const updateUser = (nextUser) => setState((current) => ({ ...current, user: typeof nextUser === 'function' ? nextUser(current.user) : nextUser }));

  const value = { ...state, refresh, signIn, signOut, updateUser };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used within AuthProvider');
  return value;
};
